import { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import type { SessionTimerConfig } from "@autopic/shared";
import styles from "./SessionTimer.module.css";

interface SessionTimerProps {
  config: SessionTimerConfig;
  /** Pause/hide once the booking is confirmed. */
  active: boolean;
}

/**
 * Gentle session-timer banner ("We're holding your slot — 14:32"). Counts down
 * from config.minutes; turns urgent (not aggressive) under one minute. Honours
 * prefers-reduced-motion by skipping the pulse.
 */
export function SessionTimer({ config, active }: SessionTimerProps) {
  const total = config.minutes * 60;
  const [remaining, setRemaining] = useState(total);
  const endRef = useRef<number>(Date.now() + total * 1000);

  useEffect(() => {
    if (!config.enabled || !active) return;
    endRef.current = Date.now() + total * 1000;
    setRemaining(total);
    const id = window.setInterval(() => {
      const left = Math.max(0, Math.round((endRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [config.enabled, active, total]);

  if (!config.enabled || !active) return null;

  const mm = Math.floor(remaining / 60)
    .toString()
    .padStart(2, "0");
  const ss = (remaining % 60).toString().padStart(2, "0");
  const expired = remaining <= 0;
  const urgent = remaining > 0 && remaining <= 60;

  return (
    <div
      className={[styles.banner, urgent ? styles.urgent : "", expired ? styles.expired : ""]
        .filter(Boolean)
        .join(" ")}
      role="status"
    >
      <Clock size={15} aria-hidden="true" />
      {expired ? (
        <span>Your held slot has expired — no rush, just pick a new time.</span>
      ) : (
        <span>
          We&rsquo;re holding your place — <strong>{mm}:{ss}</strong>
        </span>
      )}
    </div>
  );
}
